import React, { useState, useEffect } from 'react';
import { Authenticator, View, Text, useAuthenticator } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';
import { resetPassword, confirmResetPassword, signUp } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { isAllowedPersonalEmail } from '../utils/disposableEmails';

// Force labels to be consistent only where needed
I18n.putVocabulariesForLanguage('en', {
  'Email Address': 'Email Address',
});

const components = {
  Header() {
    return (
      <View textAlign="center" padding="large">
        <div className="flex justify-center mb-8">
          <img 
            src="/logo_trans_1x1_white.png" 
            alt="Dopove Logo" 
            className="w-16 h-16 opacity-90 hue-rotate-180" // Ensure it renders correctly sized
          />
        </div>
        <h1 className="text-3xl font-light tracking-widest uppercase mb-2 text-white/90">Dopove Authentication</h1>
        <p className="text-slate-400 mb-8 font-light tracking-wide text-sm">Secure access to the Dopove node</p>
      </View>
    );
  },
  ForgotPassword: {
    Header() {
      return (
        <View textAlign="center" padding="medium">
          <Text variation="warning" fontWeight="bold" fontSize="0.8rem">
            IF YOU JOINED USING GOOGLE:
          </Text>
          <Text variation="tertiary" fontSize="0.8rem">
            Please login using ur Google Account instead of resetting password.
          </Text>
        </View>
      );
    },
  },
  SignUp: {
    Header() {
      return (
        <View textAlign="center" padding="medium">
          <Text variation="tertiary" fontSize="0.8rem">
            Verification is mandatory for all Dopove nodes.
          </Text>
        </View>
      );
    },
    FormFields() {
      const [marketing, setMarketing] = useState(false);
      const [terms, setTerms] = useState(false);

      return (
        <>
          {/* Standard fields */}
          <Authenticator.SignUp.FormFields />

          {/* Custom premium checkboxes */}
          <div className="flex flex-col gap-4 mt-6 mb-4">
            <label className="flex items-start gap-4 cursor-pointer group/check">
              <div className="relative mt-1">
                <input 
                  type="checkbox" 
                  id="marketing-checkbox"
                  name="custom:marketing_consent"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="w-5 h-5 border-2 border-slate-700 rounded-md bg-slate-900 hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)] peer-checked:bg-[#5eead4] peer-checked:border-[#5eead4] group-hover/check:border-slate-500" />
                <svg className="absolute top-1 left-1 w-3 h-3 text-slate-900 opacity-0 hover-scale-press focus-visible-ring transition-[opacity,transform] duration-200 ease-[var(--ease-out)] peer-checked:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-[12px] font-bold text-white group-hover/check:text-[#5eead4] transition-colors">Marketing & Technical Updates</span>
                <span className="text-[10px] text-slate-500 leading-tight mt-0.5">I'd like to receive updates about ICE <span className="opacity-60">(Optional)</span></span>
              </div>
            </label>

            <label className="flex items-start gap-4 cursor-pointer group/check">
              <div className="relative mt-1">
                <input 
                  type="checkbox" 
                  id="terms-checkbox"
                  name="custom:terms_accepted"
                  required
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="w-5 h-5 border-2 border-slate-700 rounded-md bg-slate-900 hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)] peer-checked:bg-[#5eead4] peer-checked:border-[#5eead4] group-hover/check:border-slate-500" />
                <svg className="absolute top-1 left-1 w-3 h-3 text-slate-900 opacity-0 hover-scale-press focus-visible-ring transition-[opacity,transform] duration-200 ease-[var(--ease-out)] peer-checked:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-[12px] font-bold text-white group-hover/check:text-[#5eead4] transition-colors">Agreements <span className="text-red-500">*</span></span>
                <span className="text-[10px] text-slate-500 leading-tight mt-0.5">
                  I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#5eead4] hover:underline">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#5eead4] hover:underline">Privacy Policy</a>
                </span>
              </div>
            </label>
          </div>
        </>
      );
    }
  },
  VerifyContact: {
    Header() {
      return (
        <View textAlign="center" padding="large">
          <Text variation="primary" fontSize="1.2rem" fontWeight="bold">Account Verification Required</Text>
        </View>
      );
    },
    Footer() {
      return <View textAlign="center" padding="medium"><Text fontSize="0.7rem" color="gray">Skip is disabled for security compliance.</Text></View>;
    }
  },
  ConfirmSignUp: {
    Header() {
      return (
        <View textAlign="center" padding="large">
          <Text variation="primary" fontSize="1.2rem" fontWeight="bold">Enter Verification Code</Text>
        </View>
      );
    },
    Footer() {
      return <View textAlign="center" padding="medium"><Text fontSize="0.7rem" color="gray">Check your inbox (and spam) for the Dopove Sentinel code.</Text></View>;
    }
  }
};

export default function AuthPage() {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialMode = params.get('state') || 'signIn';
  const redirect = params.get('redirect') || '/profile';

  useEffect(() => {
    if (authStatus === 'authenticated') {
      navigate(redirect);
    }
  }, [authStatus, navigate, redirect]);

  const formFields = {
    signUp: {
      name: {
        order: 1,
        placeholder: 'Enter your full name',
        isRequired: true,
        label: 'Name',
      },
      username: {
        order: 2,
        label: 'Username',
        placeholder: 'Choose a username',
      },
      email: {
        order: 3,
        placeholder: 'name@example.com',
        labelHidden: false,
      },
      password: {
        order: 4,
        placeholder: 'Choose a strong password',
      },
      confirm_password: {
        order: 5,
        placeholder: 'Confirm your password',
      },
    },
    signIn: {
      username: {
        label: 'Username or Email',
        placeholder: 'Enter your Username or Email',
      },
    },
    forgotPassword: {
      username: {
        label: 'Username or Email',
        placeholder: 'Enter your Username or Email',
      },
    },
    resetPassword: {
      username: {
        label: 'Username or Email',
        placeholder: 'Enter your Username or Email',
      },
    },
    confirmResetPassword: {
      confirmation_code: {
        label: 'Reset Code',
        placeholder: 'Enter the code sent to your email',
      },
      password: {
        label: 'New Password',
        placeholder: 'Enter your new secure password',
      },
      confirm_password: {
        label: 'Confirm New Password',
        placeholder: 'Please confirm your new password',
      },
    },
  };

  // Query params and redirect target initialized at top of component

  return (
    <>
      <SEO title="Sign in" description="Sign in to your Dopove account." canonical="https://dopove.com/auth" noIndex />
    <div className="bg-[#191d2b] min-h-screen flex flex-col font-sans text-white">
      
      <main className="flex-grow flex items-center justify-center py-20 px-6">
        <div className="w-full max-w-xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#23263a] border border-slate-700/50 rounded-[2.5rem] p-4 md:p-10 shadow-2xl"
          >
            <Authenticator 
              initialState={initialMode === 'signUp' ? 'signUp' : 'signIn'}
              components={components}
              formFields={formFields}

              signUpAttributes={['name', 'email', 'custom:marketing_consent', 'custom:terms_accepted']}
              socialProviders={['google']}
              services={{
                async handleSignUp(input) {
                  const { options } = input;
                  const email = options?.userAttributes?.email;
                  
                  // Manually capture the checkbox values from the DOM since we removed them from managed formFields
                  const termsAccepted = document.getElementById('terms-checkbox')?.checked;
                  const marketingConsent = document.getElementById('marketing-checkbox')?.checked;
                  
                  if (!termsAccepted) {
                    throw new Error('You must agree to the Terms of Service to create an account.');
                  }

                  if (!email || !isAllowedPersonalEmail(email)) {
                    throw new Error('Invalid email: Please use a personal email address (e.g., Gmail, Outlook, Yahoo) to sign up.');
                  }

                  // Inject the values into the user attributes before calling signUp
                  input.options.userAttributes['custom:terms_accepted'] = termsAccepted ? 'true' : 'false';
                  input.options.userAttributes['custom:marketing_consent'] = marketingConsent ? 'true' : 'false';

                  return signUp(input);
                },
                async handleForgotPassword(input) {
                  const { username } = input;
                  const trimmed = username.trim();
                  
                  // 1. Professional Validation: Spaces are NOT allowed in Cognito usernames/emails
                  if (trimmed.includes(' ')) {
                    throw new Error("Spaces are not allowed in Username or Email. Please check your entry.");
                  }

                  try {
                    return await resetPassword({ username: trimmed });
                  } catch (error) {
                    // 2. SSO / Federated User Detection
                    // Cognito typical errors for federated users: 'UserNotFoundException' or 'NotAuthorizedException'
                    // We check if the error message mentions federated or just provide a catch-all hint
                    if (error.name === 'UserNotFoundException' || error.name === 'NotAuthorizedException') {
                      throw new Error("ACCOUNT NOT FOUND: If you joined using Google, please use the 'Sign In with Google' button instead.");
                    }
                    throw error;
                  }
                },
                async handleForgotPasswordSubmit(input) {
                  const { username, confirmationCode, password } = input;
                  return confirmResetPassword({
                    username: username.trim(),
                    confirmationCode,
                    password,
                  });
                },
              }}
            >
              {({ signOut, user }) => (
                <div className="text-center p-8">
                  <h1 className="text-3xl font-bold mb-4 text-white uppercase tracking-tight">Access Granted</h1>
                  <p className="mb-4 text-slate-400">Node Authenticated: {user?.username}</p>
                  <p className="mb-8 text-xs font-mono text-teal-500/70">{user?.signInDetails?.loginId}</p>
                  <div className="flex flex-col gap-4">
                  <button
                    onClick={() => navigate('/profile')}
                    className="px-8 py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white font-black uppercase tracking-widest text-xs rounded-xl hover-scale-press focus-visible-ring transition-[transform,box-shadow] duration-200 ease-[var(--ease-out)] shadow-lg"
                  >
                    Manage Account
                  </button>
                    <button
                      onClick={signOut}
                      className="px-8 py-3 border border-slate-700 text-slate-300 font-bold rounded-xl hover-scale-press focus-visible-ring transition-[background-color,transform] duration-200 ease-[var(--ease-out)] hover:bg-slate-700/30"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </Authenticator>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
}
